import { useEffect, useMemo, useRef, useState } from 'react';

/** What the edit pages read off the booking for its class (`RentalBookingDetails`). */
export interface StoredBookingClass {
    id?: string;
    /** The operator's override; null = none. Absent on an older backend. */
    customerClassId?: string | null;
    /** The class a cleared override would price under (`noOverrideClassId`). */
    noOverrideCustomerClassId?: string | null;
}

/**
 * The customer class on a room or venue EDIT form (staging finding 6): the
 * same `BookingCustomerClassSelect` the create forms use, pre-filled with the
 * booking's stored override.
 *
 * Seeded ONCE per booking from the API, as the attribution beside it is —
 * afterwards the form owns it, so a background refetch cannot overwrite what
 * the operator picked. Seeded only from a response that CARRIES the field: an
 * older backend sends none, and then the class is neither shown nor sent, so
 * a save can never clear an override it did not read.
 *
 * `classFields` is what the save body and the price preview carry: the picked
 * class once seeded, else nothing. The backend reprices only when it differs
 * from the stored override (`editClassChange`), so sending the unchanged value
 * moves nothing.
 *
 * `noOverrideClassId` names the select's "no override" entry: the class a
 * cleared override would price the booking under, as the backend's edit
 * resolver reads it (for a web booking its own member or covering class, for
 * a staff booking the account/organisation chain). Read from the same
 * response, so the label and the save cannot disagree.
 */
export const useEditBookingCustomerClass = (
    data: StoredBookingClass | undefined,
    orderId: string,
) => {
    const [customerClassId, setCustomerClassId] = useState<string | null>(null);
    const [seededFor, setSeededFor] = useState<string | null>(null);
    const seededRef = useRef<string | null>(null);

    useEffect(() => {
        if (!data || data.id !== orderId || data.customerClassId === undefined) return;
        if (seededRef.current === orderId) return;
        seededRef.current = orderId;
        setCustomerClassId(data.customerClassId ?? null);
        setSeededFor(orderId);
    }, [data, orderId]);

    const seeded = seededFor === orderId;
    const classFields = useMemo<{ customerClassId?: string | null }>(
        () => (seeded ? { customerClassId } : {}),
        [seeded, customerClassId],
    );

    const noOverrideClassId = data?.id === orderId ? (data?.noOverrideCustomerClassId ?? null) : null;

    return { customerClassId, setCustomerClassId, seeded, classFields, noOverrideClassId };
};
